/**
 * CandidateFinder - Unpublished Referrals & Manual Matching
 * Updated: 2025-10-16 - Fixed selectedProviderForMatching state issue
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Search, Users, MapPin, Star, Filter, AlertCircle, Loader2, CheckCircle, Clock, FileText, Phone, Mail, MessageSquare, X } from "lucide-react";
import { config } from "@/src/config";
import { CommunicationMethodSelector } from "./CommunicationMethodSelector";
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

// Types for API integration
interface CBOMatch {
  cbo_id: string;
  name: string;
  score: number;
}

interface ReferralData {
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  date_of_birth: string;
  zip_code: string;
  preferred_language: string;
  medicaid_id: string;
  health_plan: string;
  plan_type: string;
  provider_name: string;
  npi_number: string;
  organization: string;
  provider_phone_number: string;
  primary_diagnosis: string;
  risk_score?: number;
  needs: string[];
  availability: string;
  urgency_level: string;
  additional_notes?: string;
  prior_authorization_required: boolean;
  member_consent_obtained: boolean;
}

interface Navigator {
  id: string;
  name: string;
  organization: string;
  distance: string;
  trustScore: number;
  capacity: number;
}

interface CandidateFinderProps {
  onNavigateToSubmissions?: () => void;
  onBack?: () => void;
  referralData?: ReferralData;
  referrals?: any[]; // Add referrals prop
  onMatchReferral?: (referralId: string, providerId: string) => void; // Add matching function
  onNavigateToDirectory?: () => void; // Add directory navigation
  onStartManualMatching?: (referral: any) => void; // Callback when user starts manual matching
  selectedReferralForMatching?: any | null; // Referral being matched (from App state)
  selectedProviderForMatching?: any | null; // Provider selected (from App state)
  onRefreshReferrals?: () => void; // Callback to refresh referrals list
}

export function CandidateFinder({
  onNavigateToSubmissions,
  onBack,
  referralData,
  referrals,
  onNavigateToDirectory,
  onStartManualMatching,
  selectedReferralForMatching: externalSelectedReferral,
  selectedProviderForMatching: externalSelectedProvider,
  onRefreshReferrals
}: CandidateFinderProps) {
  const { fmt } = useFormattedDate();
  const [filters, setFilters] = useState({
    zipCode: "",
    distance: "10 miles",
    sdohNeeds: ""
  });
  
  // API integration state
  const [matches, setMatches] = useState<CBOMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Feedback state
  const [feedbackData, setFeedbackData] = useState<Record<string, any>>({});
  const [feedbackLoading, setFeedbackLoading] = useState<Record<string, boolean>>({});
  const [feedbackSuccess, setFeedbackSuccess] = useState<Record<string, boolean>>({});

  // Provider selection state for in-page workflow
  const [selectedReferralForMatching, setSelectedReferralForMatching] = useState<any | null>(externalSelectedReferral);
  const [selectedProviderForMatching, setSelectedProviderForMatching] = useState<any | null>(externalSelectedProvider);
  const [selectedProvider, setSelectedProvider] = useState<any | null>(externalSelectedProvider);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Update local state when external props change (when user returns from provider directory)
  useEffect(() => {
    if (externalSelectedReferral) {
      setSelectedReferralForMatching(externalSelectedReferral);
    }
    if (externalSelectedProvider) {
      setSelectedProviderForMatching(externalSelectedProvider);
      setSelectedProvider(externalSelectedProvider);
      setShowConfirmation(true); // Automatically show confirmation dialog
    }
  }, [externalSelectedReferral, externalSelectedProvider]);

  // Filter unpublished referrals that need manual matching
  // Show referrals where use_matching_engine is false (manual provider selection)
  const unpublishedReferrals = referrals?.filter(referral =>
    !referral.useMatchingEngine &&
    referral.status === "Pending"
  ) || [];

  // Get recently matched providers for quick selection
  const recentlyMatchedProviders = referrals?.filter(referral =>
    referral.status === "Matched"
  ).slice(0, 5).map(r => ({
    id: r.id,
    name: r.referringProvider?.name || 'Unknown Provider',
    organization: r.referringProvider?.organization || '',
    lastMatched: r.createdAt
  })) || [];

  // Mock navigators removed - these will come from provider directory
  const navigators: Navigator[] = [];

  // Generate a referral ID for tracking
  const generateReferralId = () => {
    return `REF${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // API function to fetch matches
  const fetchMatches = async (referralData: ReferralData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${config.api.baseUrl}/match_referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(referralData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMatches(data);
      setHasSearched(true);
    } catch (err) {
      console.error('Error fetching matches:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch matches');
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  // API function to submit feedback
  const submitFeedback = async (cboId: string, status: string, notes?: string) => {
    const referralId = generateReferralId();
    setFeedbackLoading(prev => ({ ...prev, [cboId]: true }));
    setFeedbackSuccess(prev => ({ ...prev, [cboId]: false }));

    try {
      const response = await fetch(`${config.api.baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referral_id: referralId,
          cbo_id: cboId,
          status: status,
          notes: notes
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setFeedbackSuccess(prev => ({ ...prev, [cboId]: true }));
        setFeedbackData(prev => ({
          ...prev,
          [cboId]: {
            referral_id: referralId,
            cbo_id: cboId,
            status: status,
            notes: notes
          }
        }));
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setFeedbackSuccess(prev => ({ ...prev, [cboId]: false }));
        }, 3000);
      } else {
        throw new Error(data.message || 'Failed to submit feedback');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setFeedbackLoading(prev => ({ ...prev, [cboId]: false }));
    }
  };

  // Test with mock data
  const testWithMockData = () => {
    const mockReferralData: ReferralData = {
      first_name: "Jane",
      last_name: "Doe",
      phone_number: "(314) 555-1212",
      email: "jane.doe@example.com",
      date_of_birth: "1985-06-12",
      zip_code: "63130",
      needs: ["housing", "food"],
      medicaid_id: "12345",
      health_plan: "MO HealthNet",
      plan_type: "Managed Care",
      urgency_level: "Routine",
      provider_name: "Dr. Smith",
      npi_number: "1234567890",
      organization: "St. Louis Clinic",
      provider_phone_number: "314-555-1212",
      preferred_language: "English",
      primary_diagnosis: "Hypertension",
      risk_score: 60,
      availability: "Weekdays 9-5",
      additional_notes: "Needs housing urgently",
      prior_authorization_required: false,
      member_consent_obtained: true
    };
    
    fetchMatches(mockReferralData);
  };

  const handleSearch = () => {
    if (referralData) {
      fetchMatches(referralData);
    } else {
      testWithMockData();
    }
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < fullStars
                ? "text-yellow-500 fill-current"
                : i === fullStars && hasHalfStar
                ? "text-yellow-500 fill-current opacity-50"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const renderScore = (score: number) => {
    const percentage = Math.round(score * 100);
    let colorClass = "text-red-600";
    if (percentage >= 80) colorClass = "text-green-600";
    else if (percentage >= 60) colorClass = "text-yellow-600";
    
    return (
      <div className="text-center">
        <div className={`text-2xl font-bold ${colorClass}`}>
          {percentage}%
        </div>
        <div className="text-xs text-gray-600">Match Score</div>
      </div>
    );
  };

  const renderFeedbackControls = (cboId: string) => {
    const currentFeedback = feedbackData[cboId];
    const isLoading = feedbackLoading[cboId];
    const isSuccess = feedbackSuccess[cboId];
    
    return (
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
        <h4 className="font-semibold text-gray-800">Track Outcome</h4>
        
        {isSuccess && (
          <div className="flex items-center space-x-2 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>Feedback recorded successfully!</span>
          </div>
        )}
        
        {currentFeedback ? (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Status:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentFeedback.status === 'accepted' ? 'bg-green-100 text-green-800' :
                currentFeedback.status === 'rejected' ? 'bg-red-100 text-red-800' :
                currentFeedback.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {currentFeedback.status.toUpperCase()}
              </span>
            </div>
            {currentFeedback.notes && (
              <div className="text-sm text-gray-600">
                <strong>Notes:</strong> {currentFeedback.notes}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => submitFeedback(cboId, 'accepted')}
                disabled={isLoading}
                className="bg-v2accent hover:bg-v2accent/90 text-white text-xs py-1 h-8"
              >
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : '✓ Accepted'}
              </Button>
              <Button
                onClick={() => submitFeedback(cboId, 'rejected')}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-700 text-white text-xs py-1 h-8"
              >
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : '✗ Rejected'}
              </Button>
              <Button
                onClick={() => submitFeedback(cboId, 'completed')}
                disabled={isLoading}
                className="bg-v2accent hover:bg-v2accent/90 text-white text-xs py-1 h-8"
              >
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : '✓ Completed'}
              </Button>
              <Button
                onClick={() => submitFeedback(cboId, 'failed')}
                disabled={isLoading}
                className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs py-1 h-8"
              >
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : '⚠ Failed'}
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-700">Add Notes (Optional)</Label>
              <Textarea
                placeholder="Add notes about the outcome..."
                value={feedbackData[cboId]?.notes || ""}
                className="text-xs h-16 resize-none"
                onChange={(e) => {
                  const notes = e.target.value;
                  setFeedbackData(prev => ({
                    ...prev,
                    [cboId]: {
                      ...prev[cboId],
                      notes: notes
                    }
                  }));
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-paper p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
          {/* Toolbar */}
          {onBack && (
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <button
                onClick={onBack}
                className="p-2 hover:bg-paper-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-ink-500" />
              </button>
            </div>
          )}

          {/* Selected Provider from My Providers - Show when provider selected but no referral picked yet */}
          {selectedProviderForMatching && !selectedReferralForMatching && (
            <Card className="mb-6 border border-v2border shadow-sm bg-surface rounded-lg overflow-hidden">
              <CardHeader className="pb-4 bg-v2accent-soft border-b border-v2border">
                <CardTitle className="flex items-center space-x-3 text-lg font-semibold text-v2accent-ink">
                  <div className="p-2 bg-v2accent rounded-lg">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div>Provider Selected from My Providers</div>
                    <div className="text-sm font-normal text-ink-500 mt-1">
                      {selectedProviderForMatching.organization_name} - {selectedProviderForMatching.organization_type}
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-v2accent-soft border border-v2border rounded-xl p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-v2accent-ink flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-v2accent-ink mb-1">Select a referral below to match with this provider</p>
                      <p className="text-sm text-v2accent-ink">
                        {selectedProviderForMatching.organization_name} is ready to be matched. Choose an unpublished referral from the list below.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Contact</p>
                    <p className="text-sm text-gray-800">{selectedProviderForMatching.primary_phone}</p>
                    {selectedProviderForMatching.primary_email && (
                      <p className="text-sm text-gray-800">{selectedProviderForMatching.primary_email}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Location</p>
                    <p className="text-sm text-gray-800">{selectedProviderForMatching.city}, {selectedProviderForMatching.state}</p>
                    {selectedProviderForMatching.distance_miles && (
                      <p className="text-sm text-gray-600">{selectedProviderForMatching.distance_miles.toFixed(1)} miles away</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setSelectedProviderForMatching(null)}
                  className="w-full border border-v2border bg-surface text-ink hover:bg-paper-2 rounded-xl"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Clear Selection
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Unpublished Referrals Section */}
          {unpublishedReferrals.length > 0 && (
            <Card className="mb-6 border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
              <CardHeader className="pb-6 border-b border-v2border rounded-t-3xl">
                <CardTitle className="flex items-center space-x-3 text-xl font-semibold text-ink">
                  <div className="p-3 bg-paper-2 rounded-2xl">
                    <AlertCircle className="w-6 h-6 text-ink-500" />
                  </div>
                  <span>Unpublished Referrals - Manual Matching Required</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {unpublishedReferrals.map((referral) => (
                    <div key={referral.id} className="p-4 bg-surface border border-v2border rounded-xl hover:bg-paper-2 transition-all duration-300">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-8 h-8 bg-paper-2 rounded-lg flex items-center justify-center">
                              <Users className="w-4 h-4 text-ink-500" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg text-gray-800">{referral.memberName}</h3>
                              <p className="text-sm text-gray-600">Medicaid ID: {referral.memberMedicaidId}</p>
                            </div>
                          </div>
                          <div className="ml-11 space-y-1">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Needs:</span> {referral.sdohNeeds.join(", ")}
                            </p>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Urgency:</span> {referral.urgencyLevel}
                            </p>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Location:</span> {referral.zipCode}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {selectedProviderForMatching ? (
                            // If provider is already selected from My Providers, show Match button
                            <Button
                              onClick={() => {
                                setSelectedReferralForMatching(referral);
                                // Set the provider for confirmation dialog with all contact fields
                                console.log('🔍 Setting provider from selectedProviderForMatching:', selectedProviderForMatching);
                                setSelectedProvider({
                                  id: selectedProviderForMatching.id,
                                  provider_id: selectedProviderForMatching.provider_id,
                                  name: selectedProviderForMatching.organization_name || selectedProviderForMatching.name,
                                  organization: selectedProviderForMatching.organization_type || selectedProviderForMatching.organization,
                                  organization_name: selectedProviderForMatching.organization_name,
                                  organization_type: selectedProviderForMatching.organization_type,
                                  phone: selectedProviderForMatching.phone,
                                  phone_number: selectedProviderForMatching.phone_number,
                                  primary_phone: selectedProviderForMatching.primary_phone,
                                  primary_email: selectedProviderForMatching.primary_email,
                                  email: selectedProviderForMatching.email,
                                  website: selectedProviderForMatching.website,
                                  address: selectedProviderForMatching.address,
                                });
                                setShowConfirmation(true);
                              }}
                              className="bg-v2accent hover:bg-v2accent/90 text-white border-0 transition-all duration-300 rounded-xl px-6 py-3 text-sm font-bold active:scale-95 flex items-center space-x-2"
                            >
                              <CheckCircle className="w-5 h-5 text-white" />
                              <span>Match with {selectedProviderForMatching.organization_name}</span>
                            </Button>
                          ) : (
                            // Normal flow: select provider from directory
                            <Button
                              onClick={() => {
                                if (onStartManualMatching) {
                                  onStartManualMatching(referral);
                                } else {
                                  setSelectedReferralForMatching(referral);
                                }
                              }}
                              className="bg-v2accent hover:bg-v2accent/90 text-white border-0 transition-all duration-300 rounded-xl px-6 py-3 text-sm font-bold active:scale-95 flex items-center space-x-2"
                            >
                              <Users className="w-5 h-5 text-white" />
                              <span>Select Provider</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* In-Page Provider Directory - Shows when referral selected for matching */}
          {selectedReferralForMatching && (
            <Card className="mb-6 border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
              <CardHeader className="pb-4 bg-v2accent-soft border-b border-v2border">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-3 text-xl font-bold text-v2accent-ink">
                    <div className="p-3 bg-v2accent rounded-2xl">
                      <Search className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div>Select Provider for {selectedReferralForMatching.memberName}</div>
                      <div className="text-sm font-normal text-ink-500 mt-1">
                        Needs: {selectedReferralForMatching.sdohNeeds.join(", ")}
                      </div>
                    </div>
                  </CardTitle>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedReferralForMatching(null)}
                    className="rounded-xl"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Referrals
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <Button
                    onClick={() => {
                      if (onNavigateToDirectory) {
                        onNavigateToDirectory();
                      }
                    }}
                    className="bg-v2accent hover:bg-v2accent/90 text-white rounded-xl px-6 py-3"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Open Full Provider Directory
                  </Button>
                </div>

                {/* Provider Search Box */}
                <div className="mb-6">
                  <Label className="text-base font-bold text-gray-800 mb-2 block">Quick Provider Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    {/* Same autofill exposure as the roster search (TRUS-444): an
                        unlabelled text input whose placeholder says "name" reads to
                        Chrome as a contact field, so it offers autofill over the box. */}
                    <Input
                      type="search"
                      name="provider-quick-query"
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      placeholder="Search by provider name or organization..."
                      className="pl-10 border border-v2border focus:border-v2accent rounded-xl"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Tip: Use the full directory for advanced search with filters and map view</p>
                </div>

                {/* Recently Matched Providers */}
                {recentlyMatchedProviders.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Clock className="w-5 h-5 text-ink-500" />
                      <h3 className="text-lg font-bold text-ink">Recently Matched Providers</h3>
                    </div>
                    <div className="space-y-3">
                      {recentlyMatchedProviders.map((provider) => (
                        <div
                          key={provider.id}
                          className="p-4 bg-surface border border-v2border rounded-xl hover:bg-paper-2 transition-all duration-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-800">{provider.name}</h4>
                              <p className="text-sm text-gray-600">{provider.organization}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Last matched: {fmt.short(provider.lastMatched)}
                              </p>
                            </div>
                            <Button
                              onClick={() => {
                                console.log('🔍 Setting provider from My Providers:', provider);
                                setSelectedProvider({
                                  ...provider, // Include all provider fields
                                  organization_name: provider.name,
                                  organization_type: provider.organization,
                                });
                                setShowConfirmation(true);
                              }}
                              className="bg-v2accent hover:bg-v2accent/90 text-white rounded-xl px-4 py-2 text-sm font-bold"
                            >
                              <Users className="w-4 h-4 mr-2" />
                              Select
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {recentlyMatchedProviders.length === 0 && (
                  <div className="text-center py-8 text-gray-600">
                    <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="mb-2 font-semibold">No Recently Matched Providers</p>
                    <p className="text-sm text-gray-500">Use the full directory above to search for providers</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Confirmation Dialog */}
          {showConfirmation && selectedProvider && selectedReferralForMatching && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="w-full max-w-2xl my-8 min-h-0 flex">
                <Card className="w-full border border-v2border shadow-2xl bg-surface rounded-lg max-h-[90vh] flex flex-col">
                <CardHeader className="pb-4 bg-paper-2 border-b border-v2border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center space-x-3 text-xl font-bold text-ink">
                      <CheckCircle className="w-8 h-8 text-v2accent" />
                      <span>Confirm Referral Submission</span>
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowConfirmation(false);
                        setSelectedProvider(null);
                        setSelectedProviderForMatching(null);
                        setSelectedReferralForMatching(null);
                      }}
                      className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full p-2"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-6 overflow-y-auto flex-1">
                  <div className="space-y-6">
                    {/* Referral Summary */}
                    <div className="p-4 bg-paper-2 rounded-xl border border-v2border">
                      <h3 className="font-bold text-ink mb-2">Member</h3>
                      <p className="text-gray-700">{selectedReferralForMatching.memberName}</p>
                      <p className="text-sm text-gray-600">Medicaid ID: {selectedReferralForMatching.memberMedicaidId}</p>
                      <p className="text-sm text-gray-600">Needs: {selectedReferralForMatching.sdohNeeds.join(", ")}</p>
                    </div>

                    {/* Provider Summary */}
                    <div className="p-4 bg-paper-2 rounded-xl border border-v2border">
                      <h3 className="font-bold text-ink mb-2">Selected Provider</h3>
                      <p className="text-gray-700">{selectedProvider?.name || selectedProvider?.organization_name || 'Unknown Provider'}</p>
                      <p className="text-sm text-gray-600">{selectedProvider?.organization || selectedProvider?.organization_type || 'Community Provider'}</p>
                    </div>

                    {/* Communication Method Selection with Phase 2 Enhancements */}
                    <CommunicationMethodSelector
                      providerId={selectedProvider.provider_id || selectedProvider.id}
                      providerName={selectedProvider?.name || selectedProvider?.organization_name || 'Unknown Provider'}
                      referralId={selectedReferralForMatching.id}
                      fallbackContacts={{
                        phone: selectedProvider?.primary_phone || selectedProvider?.phone_number || selectedProvider?.phone,
                        email: selectedProvider?.primary_email || selectedProvider?.email,
                        website: selectedProvider?.website,
                        address: selectedProvider?.street_address || selectedProvider?.address
                      }}
                      onSend={async (method, message) => {
                        try {
                          console.log('🔄 Matching referral to provider...');
                          console.log('  Referral ID:', selectedReferralForMatching.id);
                          console.log('  Provider ID:', selectedProvider.id);
                          console.log('  Communication method:', method.method_type);
                          console.log('  Contact value:', method.contact_value);

                          const matchPayload = {
                            provider_id: selectedProvider.provider_id || selectedProvider.id,
                            provider_name: selectedProvider?.name || selectedProvider?.organization_name,
                            provider_organization: selectedProvider.organization_type || selectedProvider.organization || 'Community Provider',
                            communication_method: method.method_type
                          };

                          console.log('  Match payload being sent:', JSON.stringify(matchPayload, null, 2));

                          // Use the dedicated match endpoint
                          const response = await authFetch(`${config.api.baseUrl}/api/referrals/${selectedReferralForMatching.id}/match`, {
                            method: 'PUT',
                            jsonBody: matchPayload
                          });

                          console.log('  Response status:', response.status, response.statusText);

                          if (response.ok) {
                            const data = await response.json();
                            console.log('✅ Referral matched successfully:', data);

                            // If web form selected, submit the form automatically
                            if (method.method_type === 'web_form') {
                              try {
                                console.log('📝 Submitting web form for provider...');
                                const webFormResponse = await authFetch(`${config.api.baseUrl}/api/providers/${selectedProvider.provider_id || selectedProvider.id}/submit-web-form`, {
                                  method: 'POST',
                                  jsonBody: {
                                      referral_id: selectedReferralForMatching.id
                                    }
                                });

                                const webFormResult = await webFormResponse.json();

                                if (webFormResponse.ok && webFormResult.success) {
                                  console.log('✅ Web form submitted successfully:', webFormResult);
                                } else {
                                  console.warn('⚠️ Web form submission failed (non-critical):', webFormResult.message);
                                  // Don't fail the match if web form fails
                                }
                              } catch (webFormError) {
                                console.error('⚠️ Web form submission error (non-critical):', webFormError);
                                // Don't fail the match if web form fails
                              }
                            }

                            // Save provider name before clearing state
                            const providerName = selectedProvider?.name || selectedProvider?.organization_name;
                            const providerOrg = selectedProvider?.organization || selectedProvider?.organization_type || 'Community Provider';

                            setShowConfirmation(false);
                            setSelectedProvider(null);
                            setSelectedProviderForMatching(null);  // Clear provider selection
                            setSelectedReferralForMatching(null);

                            const successMessage = method.method_type === 'web_form'
                              ? `Referral sent via web form! Status updated to Matched.\n\nProvider: ${providerName}\nOrganization: ${providerOrg}\n\nWeb form has been automatically submitted.\n\nThis match will now appear in Provider Performance.`
                              : `Referral sent via ${method.method_type}! Status updated to Matched.\n\nProvider: ${providerName}\nOrganization: ${providerOrg}\n\nThis match will now appear in Provider Performance.`;

                            alert(successMessage);

                            // Refresh referrals list to remove from unpublished
                            if (onRefreshReferrals) {
                              onRefreshReferrals();
                            }
                          } else {
                            const errorText = await response.text();
                            console.error('❌ Failed to match referral:', response.status, errorText);
                            throw new Error('Failed to match referral to provider');
                          }
                        } catch (error) {
                          console.error('❌ Error matching referral:', error);
                          throw error; // Re-throw so CommunicationMethodSelector can track failure
                        }
                      }}
                    />

                    {/* Cancel Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowConfirmation(false);
                          setSelectedProvider(null);
                          setSelectedProviderForMatching(null);  // Clear provider selection
                          setSelectedReferralForMatching(null);  // Clear referral selection
                        }}
                        className="px-6 rounded-xl"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </div>
            </div>
          )}

          {/* Empty State - Show when no unpublished referrals */}
          {unpublishedReferrals.length === 0 && (
          <Card className="mb-6 border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
            <CardContent className="p-12">
              <div className="text-center">
                <div className="mx-auto w-20 h-20 bg-success-soft rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-success" />
                </div>
                <h3 className="text-xl font-semibold text-ink mb-3">No New Unpublished Referrals</h3>
                <p className="text-ink-500 text-lg mb-2">All referrals have been matched!</p>
                <p className="text-ink-500 text-sm">Check back later for new referrals requiring manual matching.</p>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Error State */}
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50/80">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <h3 className="font-semibold text-red-800">Error</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                    <p className="text-red-600 text-xs mt-1">
                      Unable to connect to backend API. Please try again later.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          <div className="space-y-4">
            {loading ? (
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl">
                <CardContent className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-v2accent" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Finding Best Matches...</h3>
                  <p className="text-slate-600">Our AI is analyzing CBOs and calculating match scores</p>
                </CardContent>
              </Card>
            ) : hasSearched && matches.length > 0 ? (
              matches.map((match, index) => (
                <Card key={match.cbo_id} className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* CBO Info */}
                      <div className="lg:col-span-2">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-paper-2 rounded-xl flex items-center justify-center">
                            <Users className="w-6 h-6 text-ink-500" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-ink mb-1">{match.name}</h3>
                            <p className="text-slate-600 text-base font-medium">CBO ID: {match.cbo_id}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs bg-v2accent-soft text-v2accent-ink px-2 py-1 rounded-full font-medium">
                                Rank #{index + 1}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Match Score */}
                        <div className="flex items-center space-x-4">
                          {renderScore(match.score)}
                          <Button
                            onClick={onNavigateToSubmissions}
                            className="bg-v2accent hover:bg-v2accent/90 text-white border-0 transition-shadow rounded-xl px-4 py-2 text-base font-semibold active:scale-95"
                          >
                            Create Referral
                          </Button>
                        </div>
                      </div>
                      
                      {/* Feedback Controls */}
                      <div className="lg:col-span-1">
                        {renderFeedbackControls(match.cbo_id)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : hasSearched && matches.length === 0 ? (
              <Card className="border border-yellow-200/50 shadow-2xl bg-yellow-50 rounded-3xl">
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Matches Found</h3>
                  <p className="text-yellow-700">Try adjusting your search criteria or contact support</p>
                </CardContent>
              </Card>
            ) : (
              // Default state - show mock navigators
              navigators.map((navigator) => (
                <Card key={navigator.id} className="border border-v2border shadow-sm hover:shadow-md transition-shadow bg-surface rounded-xl overflow-hidden group">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-paper-2 rounded-xl flex items-center justify-center">
                          <Users className="w-6 h-6 text-ink-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-ink mb-1">{navigator.name}</h3>
                          <p className="text-slate-600 text-base font-medium">{navigator.organization}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <div className="flex items-center space-x-1 mb-1">
                            <MapPin className="w-4 h-4 text-blue-500" />
                            <span className="text-base font-semibold text-gray-800">{navigator.distance}</span>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center space-x-1 mb-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span className="text-base font-semibold text-gray-800">{navigator.trustScore}</span>
                          </div>
                          {renderStars(navigator.trustScore)}
                        </div>
                        <div className="text-center min-w-28">
                          <p className="text-xs font-semibold text-slate-600 mb-1">Capacity</p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${navigator.capacity}%` }}
                        />
                      </div>
                          <p className="text-xs font-medium text-gray-800">{navigator.capacity}%</p>
                  </div>
                  <Button
                    onClick={onNavigateToSubmissions}
                          className="bg-v2accent hover:bg-v2accent/90 text-white border-0 transition-shadow rounded-xl px-4 py-2 text-base font-semibold active:scale-95"
                  >
                    Create Referral
                  </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
    </div>
  );
}
